import { useEffect, useState } from 'react';
import { Button, Flex, Select, Typography } from 'antd';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { ShipmentsService, ToastMessage } from '@utility/index';

const { Title, Text } = Typography;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(email: string) {
  return EMAIL_REGEX.test(email.trim());
}

type InboundShipmentAttentionTabProps = {
  data: WmsShipmentInboundType;
  onRefresh: () => void;
};

export function InboundShipmentAttentionTab({
  data,
  onRefresh,
}: InboundShipmentAttentionTabProps) {
  const serverEmails = (data.attentionEmails ?? []).map(e => e.email).filter(Boolean);
  const [emails, setEmails] = useState<string[]>(serverEmails);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmails(serverEmails);
  }, [data._id, data.attentionEmails]);

  const handleChange = (value: string[]) => {
    const trimmed = value.map(v => v.trim()).filter(Boolean);
    const valid = trimmed.filter(isValidEmail);

    if (valid.length !== trimmed.length) {
      ToastMessage.error('Only valid email addresses are allowed');
      return;
    }

    setEmails(valid);
  };

  const handleSave = async () => {
    if (!data._id) return;

    const invalid = emails.filter(email => !isValidEmail(email));
    if (invalid.length > 0) {
      ToastMessage.error('Only valid email addresses are allowed');
      return;
    }

    try {
      setSaving(true);
      await ShipmentsService.updateAttentionEmails(data._id, { emails });
      ToastMessage.success('Attention emails updated');
      onRefresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    emails.length !== serverEmails.length ||
    emails.some(email => !serverEmails.includes(email)) ||
    serverEmails.some(email => !emails.includes(email));

  return (
    <Flex vertical gap={16} style={{ padding: '8px 0 0' }}>
      <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
        Email Addresses
      </Title>
      {emails.length === 0 && !saving ? (
        <Text type="secondary" style={{ fontSize: 14 }}>
          No attention emails configured.
        </Text>
      ) : null}
      <Select
        mode="tags"
        style={{ width: '100%' }}
        placeholder="Add email addresses"
        value={emails}
        onChange={handleChange}
        tokenSeparators={[',', ' ']}
        options={[]}
        disabled={saving}
      />
      <Button
        type="primary"
        onClick={handleSave}
        loading={saving}
        disabled={!hasChanges || saving}
        style={{ alignSelf: 'flex-start' }}
      >
        Save
      </Button>
    </Flex>
  );
}
